import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { ChevronLeft, LogOut, Gauge, Sparkles, Activity } from "lucide-react";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Line,
  LineChart,
} from "recharts";

const SOURCE_LABELS = {
  trackman: "TrackMan",
  "643charts": "6-4-3 Charts",
  flightscope: "FlightScope",
  rapsodo: "Rapsodo",
  yakkertech: "Yakkertech",
  kvest: "K-Vest",
  playerdata: "PlayerData",
  stalker: "Stalker",
};

function MetricChip({ label, value, unit }) {
  return (
    <div className="rounded-md ink-card p-3 text-center">
      <div className="text-[9px] tracking-[0.25em] font-bold uppercase text-white/40">{label}</div>
      <div className="mt-1 flex items-baseline justify-center gap-1">
        <span className="font-display font-black text-xl font-mono-num text-white">{value ?? "—"}</span>
        {value != null && unit && <span className="text-[10px] text-white/40">{unit}</span>}
      </div>
    </div>
  );
}

export default function PlayerTelemetry() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/players/me/ingestions");
        setItems(r.data?.items || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const last = items[0];
  const veloTrend = items
    .filter((i) => i.summary?.avg_velocity_mph != null)
    .slice(0, 14)
    .reverse()
    .map((i) => ({
      date: new Date(i.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      avg: i.summary.avg_velocity_mph,
      max: i.summary.max_velocity_mph,
    }));

  const handleLogout = async () => { await logout(); nav("/", { replace: true }); };

  return (
    <div className="min-h-screen pb-16" style={{ backgroundColor: "var(--ink-1)" }} data-testid="player-telemetry-page">
      <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-[var(--ink-border)]" style={{ backgroundColor: "rgba(10,20,36,0.85)" }}>
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo size="sm" variant="lockup" linkTo="/portal" dark />
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/70 hover:text-white hover:bg-white/5" data-testid="telemetry-logout">
            <LogOut className="h-4 w-4 mr-1.5" /> Sign out
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 pt-6 space-y-6">
        <Link to="/portal" className="inline-flex items-center text-sm text-white/50 hover:text-white">
          <ChevronLeft className="h-4 w-4" /> Back to today&apos;s log
        </Link>

        <div>
          <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Performance · TrackMan · 6-4-3 · K-Vest · …</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white mt-1">Telemetry</h1>
          <p className="text-sm text-white/50 mt-1">Every pitch your coaching staff uploads — alongside what the numbers mean for you.</p>
        </div>

        {loading ? (
          <div className="rounded-xl ink-card p-8 text-center text-white/40">Loading telemetry…</div>
        ) : items.length === 0 ? (
          <div className="rounded-xl ink-card p-8 text-center text-white/50" data-testid="telemetry-empty">
            <Gauge className="h-8 w-8 mx-auto text-white/30" />
            <div className="mt-3 text-sm">No pitch-tracking sessions uploaded yet.</div>
            <div className="mt-1 text-xs text-white/40">Once your coach uploads a TrackMan, 6-4-3 Charts, Rapsodo, or Stalker session, it&apos;ll show up here.</div>
          </div>
        ) : (
          <>
            {/* Last session card */}
            <section className="rounded-xl ink-card p-5" data-testid="telemetry-latest">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Last session · {SOURCE_LABELS[last.source] || last.source}</div>
                  <div className="text-sm text-white/60 mt-0.5">{new Date(last.created_at).toLocaleString()}</div>
                </div>
                <div className="text-xs text-white/40 font-mono-num">{last.summary?.pitches} {last.summary?.pitches === 1 ? "row" : "rows"}</div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
                <MetricChip label="Max velo" value={last.summary?.max_velocity_mph} unit="mph" />
                <MetricChip label="Avg velo" value={last.summary?.avg_velocity_mph} unit="mph" />
                <MetricChip label="Max spin" value={last.summary?.max_spin_rpm} unit="rpm" />
                <MetricChip label="Avg spin" value={last.summary?.avg_spin_rpm} unit="rpm" />
                <MetricChip label="Extension" value={last.summary?.avg_extension_ft} unit="ft" />
                <MetricChip label="Rel height" value={last.summary?.avg_release_height_ft} unit="ft" />
              </div>

              {last.takeaways && last.takeaways.length > 0 && (
                <div className="mt-4 rounded-md border border-cyan-900/40 bg-cyan-950/20 p-4" data-testid="telemetry-takeaways">
                  <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-cyan-300 mb-2 flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3" /> What this means
                  </div>
                  <ul className="space-y-1.5 text-xs text-cyan-100">
                    {last.takeaways.map((t, i) => <li key={i}>• {t}</li>)}
                  </ul>
                </div>
              )}
            </section>

            {/* Velo trend */}
            {veloTrend.length > 1 && (
              <section className="rounded-xl ink-card p-5" data-testid="telemetry-velo-trend">
                <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Velocity trend · last {veloTrend.length} sessions</div>
                <div className="h-44 mt-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={veloTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1F3354" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} domain={["dataMin - 2", "dataMax + 2"]} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #2C4570", backgroundColor: "#0F1E36", color: "#fff" }} />
                      <Line type="monotone" dataKey="max" stroke="#EF4444" strokeWidth={2.5} dot={{ r: 3 }} name="Max" />
                      <Line type="monotone" dataKey="avg" stroke="#22D3EE" strokeWidth={2.5} dot={{ r: 3 }} name="Avg" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </section>
            )}

            {/* Session history */}
            <section className="rounded-xl ink-card overflow-hidden" data-testid="telemetry-history">
              <div className="px-5 py-3 border-b border-[var(--ink-border)] flex items-center gap-2">
                <Activity className="h-4 w-4 text-white/50" />
                <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">All sessions</div>
              </div>
              <ul className="divide-y divide-[var(--ink-border)]">
                {items.map((ing) => (
                  <li key={ing.ingestion_id} className="px-5 py-3" data-testid={`telemetry-row-${ing.ingestion_id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <div className="text-sm text-white font-semibold">{SOURCE_LABELS[ing.source] || ing.source}</div>
                        <div className="text-xs text-white/40">{new Date(ing.created_at).toLocaleString()}</div>
                      </div>
                      <div className="flex items-center gap-4 text-xs">
                        <div className="text-white/60">Max <span className="font-mono-num text-white">{ing.summary?.max_velocity_mph ?? "—"}</span></div>
                        <div className="text-white/60">Avg <span className="font-mono-num text-white">{ing.summary?.avg_velocity_mph ?? "—"}</span></div>
                        <div className="text-white/40">{ing.summary?.pitches} {ing.summary?.pitches === 1 ? "row" : "rows"}</div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
